import { Link } from 'react-router-dom';
import { Check, ChevronDown, Plus, Settings, Users, Wand } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSelectedWorkspace, useWorkspaceStore } from '../../stores/workspaceStore';

/**
 * Which workspace the app is looking at, and the things done to it.
 *
 * In the header, because it governs more than one page: the study list shows
 * what is in it, uploads go to it, and the import history is the history of
 * uploads into it. A control that changes what several pages mean belongs where
 * those pages can all see it, not inside one of them.
 *
 * The same menu as the community edition's workspace switcher (TopBar): the
 * workspaces, then Manage workspaces, Manage actions and New workspace. So it
 * is there with a single workspace too: what it manages is there to manage
 * whether or not there is anything to choose between (2026-10-04).
 */
export function WorkspaceSelector(): JSX.Element | null {
  const { workspaces, current } = useSelectedWorkspace();
  const setSelected = useWorkspaceStore((s) => s.setSelected);

  if (!current) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex h-8 max-w-56 cursor-pointer items-center gap-1.5 rounded-md border px-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        aria-label={`Workspace: ${current.name}`}
      >
        <Users className="h-4 w-4 shrink-0" />
        <span className="truncate">{current.name}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-w-72">
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">Workspaces</DropdownMenuLabel>
        {workspaces.map((w) => (
          <DropdownMenuItem
            key={w.public_id}
            onSelect={() => setSelected(w.public_id)}
            className="cursor-pointer gap-2"
          >
            {/* The tick marks what you are looking at. The DEFAULT is a separate
                fact — where uploads land when nothing is chosen — and conflating
                the two made the menu say "default" about whichever row happened
                to be selected. */}
            <Check
              className={`h-4 w-4 shrink-0 ${w.public_id === current.public_id ? 'opacity-100' : 'opacity-0'}`}
            />
            <span className="truncate">{w.name}</span>
            {w.is_default && (
              <span className="ml-auto shrink-0 text-xs text-muted-foreground">default</span>
            )}
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />

        {/* asChild so these are real anchors: middle-click, cmd-click and
            "open in new tab" all work. */}
        <DropdownMenuItem asChild>
          <Link to="/workspaces">
            <Settings className="h-4 w-4" />
            Manage workspaces
          </Link>
        </DropdownMenuItem>
        {/* The selected workspace's actions: everyone in it reads them, its
            administrators change them (the page says which). */}
        <DropdownMenuItem asChild>
          <Link to="/actions">
            <Wand className="h-4 w-4" />
            Manage actions
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/workspaces?new=1">
            <Plus className="h-4 w-4" />
            New workspace
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
